import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, ListChecks, Calendar } from 'lucide-react';
import GenericExecuteQueryView from './GenericExecuteQueryView';
import GenericManageQueriesView from './GenericManageQueriesView';
import GenericSchedulerView from './GenericSchedulerView';

export default function DataSection({
  title,
  icon: Icon,
  contentType,
  executionUrlSuffix,
  isComplex = false,
}) {
  const [activeTab, setActiveTab] = useState('execute');
  const [queries, setQueries] = useState([]);
  const [isQueryLoading, setIsQueryLoading] = useState(true);
  const [queryError, setQueryError] = useState(null);

  const fetchQueries = useCallback(async () => {
    setIsQueryLoading(true);
    setQueryError(null);
    try {
      const response = await fetch(
        'https://4e352fnqs7.execute-api.eu-central-1.amazonaws.com/uatt/do-action',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
          },
          body: JSON.stringify({
            do_action: 'f_get_queries_list_data_source',
            data: { content_type: contentType },
          }),
        }
      );
      if (!response.ok) throw new Error(`API Error fetching queries: ${response.status}`);
      const result = await response.json();
      const queriesData = Array.isArray(result) ? result : result.rows || [];

      const formattedQueries = queriesData.map((q) => ({
        id: q.id,
        title: q.description,
        queryString: q.query_text,
        schedule_type: q.schedule_type,
        frequency_interval: q.frequency_interval,
      }));
      setQueries(formattedQueries);
    } catch (err) {
      console.error(`Failed to fetch ${title} queries:`, err);
      setQueries([]);
      setQueryError(err.message);
    } finally {
      setIsQueryLoading(false);
    }
  }, [contentType, title]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const handleAddQuery = async (newQueryData) => {
    const response = await fetch(
      'https://4e352fnqs7.execute-api.eu-central-1.amazonaws.com/uatt/do-action',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
        },
        body: JSON.stringify({
          do_action: 'f_create_query_data_source',
          data: { ...newQueryData, content_type: contentType },
        }),
      }
    );
    if (!response.ok) throw new Error('Failed to add query');
    await fetchQueries();
  };

  const handleDeleteQuery = async (id) => {
    const response = await fetch(
      'https://4e352fnqs7.execute-api.eu-central-1.amazonaws.com/uatt/do-action',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
        },
        body: JSON.stringify({ do_action: 'f_remove_query', data: { id } }),
      }
    );
    if (!response.ok) throw new Error('Failed to delete query');
    await fetchQueries();
  };

  const handleUpdateSchedule = async (queryId, scheduleType, frequencyInterval = null) => {
    const updateData = {
      id: queryId,
      schedule_type: scheduleType,
      frequency_interval:
        (scheduleType === 'hourly' || scheduleType === 'minutes') && frequencyInterval
          ? frequencyInterval
          : null,
    };
    const response = await fetch(
      'https://4e352fnqs7.execute-api.eu-central-1.amazonaws.com/uatt/do-action',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer 980c9cca-8fc2-11f0-8d33-02e13bb30071',
        },
        body: JSON.stringify({
          do_action: 'f_update_query_date_source',
          data: updateData,
        }),
      }
    );
    if (!response.ok) throw new Error('Failed to update schedule');
    await fetchQueries();
  };

  const handleClearSchedule = async (queryId) => {
    await handleUpdateSchedule(queryId, null, null);
  };

  const TabButton = ({ tabName, label, icon: TabIcon }) => {
    const isActive = activeTab === tabName;
    return (
      <button
        onClick={() => setActiveTab(tabName)}
        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg border-b-2 transition-colors ${
          isActive
            ? 'border-cyan-400 text-cyan-400'
            : 'border-transparent text-gray-400 hover:text-white'
        }`}
      >
        <TabIcon size={18} /> {label}
      </button>
    );
  };

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-700 flex flex-col h-full max-w-4xl mx-auto">
      <h2 className="text-3xl font-semibold text-cyan-300 mb-1 flex items-center gap-3">
        <Icon /> {title}
      </h2>
      <p className="text-gray-400 mb-6">Search, manage, and schedule {title} queries.</p>
      <div className="flex border-b border-gray-700 mb-6">
        <TabButton tabName="execute" label="Execute Query" icon={Play} />
        <TabButton tabName="manage" label="Manage Queries" icon={ListChecks} />
        <TabButton tabName="scheduler" label="Scheduler" icon={Calendar} />
      </div>
      {isQueryLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
        </div>
      ) : queryError ? (
        <div className="bg-red-900/50 text-red-300 p-4 rounded-lg border border-red-700">
          Error: {queryError}
        </div>
      ) : (
        <div>
          {activeTab === 'execute' && (
            <GenericExecuteQueryView
              queries={queries}
              contentType={contentType}
              executionUrlSuffix={executionUrlSuffix}
            />
          )}
          {activeTab === 'manage' && (
            <GenericManageQueriesView
              queries={queries}
              onAddQuery={handleAddQuery}
              onDeleteQuery={handleDeleteQuery}
              onRefresh={fetchQueries}
              isComplex={isComplex}
              contentType={contentType}
            />
          )}
          {activeTab === 'scheduler' && (
            <GenericSchedulerView
              queries={queries}
              onUpdateSchedule={handleUpdateSchedule}
              onClearSchedule={handleClearSchedule}
            />
          )}
        </div>
      )}
    </div>
  );
}

